'use client'
import React, { useState, useEffect, useRef } from 'react';
import { Link } from "@/navigation";
import { useTranslations } from 'next-intl';
import moment from 'moment';
import CustomImage from '../Image';

interface NewsCarouselProps {
	blogList: any[];
}

const NewsCarousel: React.FC<NewsCarouselProps> = ({ blogList }) => {
	const t = useTranslations();
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isPaused, setIsPaused] = useState(false);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);
	
	// 只显示前几个博客作为轮播项
	const carouselItems = blogList.slice(0, 5);
	
	if (carouselItems.length === 0) {
		return null;
	}

	const currentItem = carouselItems[currentIndex];

	const handlePrev = () => {
		setCurrentIndex((prev) => (prev === 0 ? carouselItems.length - 1 : prev - 1));
	};

	const handleNext = () => {
		setCurrentIndex((prev) => (prev === carouselItems.length - 1 ? 0 : prev + 1));
	};

	// 自动轮播
	useEffect(() => {
		if (carouselItems.length <= 1) return;

		if (!isPaused) {
			intervalRef.current = setInterval(() => {
				setCurrentIndex((prev) => (prev === carouselItems.length - 1 ? 0 : prev + 1));
			}, 5000); // 每5秒切换一次
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [isPaused, carouselItems.length]);

	return (
        <div className='bg-black/5'>
            <style dangerouslySetInnerHTML={{__html: `
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.5s ease-out;
                }
            `}} />
            <div className="container mx-auto py-8 md:py-12">
                <div 
                    className="bg-white overflow-hidden relative group cursor-pointer transition-all duration-500 ease-out transform hover:-translate-y-1" 
                    style={{
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24), 0 3px 6px rgba(0, 0, 0, 0.15), 0 5px 10px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                >
                    <div className="flex flex-col lg:flex-row">
                        {/* 左侧图片 */}
                        <div className="lg:w-1/2 w-full h-[300px] md:h-[450px] xl:h-[500px] relative overflow-hidden bg-gray-200">
                            <Link href={`/blog/${currentItem.blog_slug}`} className="block w-full h-full overflow-hidden" prefetch={false}>
                                <CustomImage
                                    key={currentIndex}
                                    src={currentItem.blog_cover_origin}
                                    alt={currentItem.blog_title}
                                    width={800}
                                    height={500}
                                    className="w-full h-full object-cover transition-transform duration-700 ease-in-out transform scale-[1.02] group-hover:scale-105"
                                />
                                {/* 蓝色遮罩 - hover时显示 */}
                                <div className="absolute inset-0 bg-mainColor/0 group-hover:bg-mainColor/20 transition-opacity duration-700 ease-in-out"></div>
                            </Link>
                        </div>

                        {/* 右侧内容 */}
                        <div className="lg:w-1/2 w-full p-6 md:p-8 lg:p-10 flex flex-col justify-between bg-white">
                            <div key={currentIndex} className="animate-fadeIn">
                                {/* 日期 - 蓝色，带下划线，hover后下划线延伸 */}
                                <Link 
                                    href={`/blog/${currentItem.blog_slug}`}
                                    className="inline-block mb-4"
                                >
                                    <div className="text-lg md:text-xl font-bold text-mainColor relative pb-1 group-hover:text-gray-900">
                                        {currentItem.blog_upload_time && moment(currentItem.blog_upload_time).format("MM/DD YYYY")}
                                        <span className="absolute left-0 bottom-0 h-0.5 bg-gray-300 w-1/3 group-hover:w-full group-hover:bg-mainColor transition-all duration-500 ease-out"></span>
                                    </div>
                                </Link>

                                {/* 标题和导航箭头 */}
                                <div className="flex items-start justify-between gap-4 mb-6">
                                    <Link href={`/blog/${currentItem.blog_slug}`} className="flex-1" prefetch={false}>
                                        <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 group-hover:text-mainColor transition-colors line-clamp-3">
                                            {currentItem.blog_title}
                                        </h2>
                                    </Link>
                                    {/* 导航箭头 */}
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <button
                                            onClick={handlePrev}
                                            className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white border border-gray-300 hover:bg-mainColor hover:border-mainColor hover:text-white transition-all duration-300 shadow-sm"
                                            aria-label="Previous"
                                        >
                                            <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={handleNext}
                                            className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white border border-gray-300 hover:bg-mainColor hover:border-mainColor hover:text-white transition-all duration-300 shadow-sm"
                                            aria-label="Next"
                                        >
                                            <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                {/* 摘要 */}
                                {currentItem.blog_excerpt && (
                                    <p className="text-sm md:text-base text-gray-600 leading-relaxed line-clamp-10 mb-6">
                                        {currentItem.blog_excerpt}
                                    </p>
                                )}
                            </div>

                            {/* More 链接 - 左对齐 */}
                            <div className="flex items-center justify-start pt-4 border-t border-gray-200">
                                <Link 
                                    href={`/blog/${currentItem.blog_slug}`}
                                    className="flex items-center gap-2 text-gray-600 group-hover:text-mainColor font-semibold text-base transition-colors duration-300"
                                >
                                    <span>More</span>
                                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
	);
};

export default NewsCarousel;

